import { css } from 'styled-components';
import { ANIMATION_CURVE, ANIMATION_DURATION, ComponentTokens, getComponentToken, getToken } from '@optimus-web/common';
import { TabContainerElementProps } from './style';
import { TabProps } from './Tab';

type State = 'active' | 'default' | 'disabled';
type Variant = 'default';

const tokens = {
    color: {
        default: {
            default: {
                active: 'textStaticPrimary',
                default: 'textStaticTertiary',
                disabled: 'textDisabled',
            },
        },
        hover: {
            default: {
                active: 'textStaticPrimary',
                default: 'textStaticPrimary',
                disabled: 'textDisabled',
            },
        },
        active: {
            default: {
                active: 'textStaticPrimary',
                default: 'textStaticPrimary',
                disabled: 'textDisabled',
            },
        },
    },
} satisfies ComponentTokens<Variant, State>;

const getTabState = ({
    variant = 'default',
    active,
    disabled,
}: Pick<TabProps, 'active' | 'disabled'> & { variant?: Variant }) => {
    let state: State = 'default';

    if (disabled) {
        state = 'disabled';
    } else if (active) {
        state = 'active';
    }

    return { variant, state };
};

export const getTabToken = getComponentToken(tokens, getTabState);

export const getUnderlineStyles = ({ underline, active, disabled, showDivider }: TabContainerElementProps) => {
    const DEFAULT_AFTER_HEIGHT = '1.5px';
    const BOLD_AFTER_HEIGHT = '2.5px';

    const AFTER = `
        content: '';
        width: 100%;
        height: ${DEFAULT_AFTER_HEIGHT};
        position: absolute;
        left: 0;
        bottom: 0;
        border-radius: 3px;
        transition-property: background-color, opacity;
        transition-duration: ${ANIMATION_DURATION.fast}ms;
        transition-timing-function: ${ANIMATION_CURVE.default};
    `;

    if (underline) {
        if (showDivider && !active) {
            return css`
                &::after {
                    ${AFTER}
                    background-color: transparent;
                }
                &:hover {
                    &::after {
                        ${AFTER}
                        height: ${BOLD_AFTER_HEIGHT};
                        background-color: ${getToken('borderStaticPrimary')};
                    }
                }
            `;
        } else if (disabled) {
            return css`
                &::after {
                    ${AFTER}
                    background-color: ${getToken('borderDisabled')};
                }
            `;
        } else if (active) {
            return css`
                &::after {
                    ${AFTER}
                    height: ${BOLD_AFTER_HEIGHT};
                    background-color: ${getToken('borderInteractivePrimaryDefault')};
                }
            `;
        }
        return css`
            &::after {
                ${AFTER}
                background-color: ${getToken('borderStaticSecondary')};
            }
            &:hover {
                &::after {
                    ${AFTER}
                    height: ${BOLD_AFTER_HEIGHT};
                    background-color: ${getToken('borderStaticPrimary')};
                }
            }
        `;
    }

    return css`
        &:not(:only-child) {
            &::after {
                ${AFTER}
                background-color: ${active ? getToken('borderInteractivePrimaryDefault') : 'transparent'};
                height: ${active ? BOLD_AFTER_HEIGHT : 0};
                opacity: ${active ? 1 : 0};
            }
        }
    `;
};
